import type { CaptionTrack, Video } from '@/types/video'

/**
 * Reading a subtitle track: parsing the file, picking the language, finding the live cue.
 *
 * Deliberately free of runtime imports, `navigator` and `window`. It is reached from a
 * component that renders during Gatsby's SSR, and it is the only part of the caption work a
 * test in this repo can touch — there is no renderer here. `tests/services/subtitles` asserts
 * both properties, because either is one convenient import away from being lost.
 */

export type SubtitleCue = {
  start: number
  end: number
  text: string
}

/**
 * Languages written right-to-left, matching `RTL_LANGUAGES` in `backend/translation/prompts.py`.
 * Kept in step with the backend rather than guessed at: it is the list the translation prompts
 * already use, and Arabic is the only one Kite ships.
 */
const RTL_LANGUAGES = ['ar']

/** The `NOTE`, `STYLE` and `REGION` blocks a WebVTT file may carry between cues. */
const METADATA_BLOCK = /^(NOTE|STYLE|REGION)\b/

const TIMESTAMP_SEPARATOR = '-->'

/** `<v Narrator>`, `<b>`, `<i>`, `<c.loud>` — markup a `Text` node would render literally. */
const INLINE_TAG = /<[^>]*>/g

/** The primary subtag, lowercased: `pt-BR` and `PT-br` are both `pt`, `en-auto` is `en`. */
const baseLanguage = (code: string | null | undefined): string => (code ?? '').toLowerCase().split('-')[0]

/**
 * `HH:MM:SS.mmm` or `MM:SS.mmm`, in seconds. `NaN` when the string is not a timestamp, which
 * is how a malformed block gets skipped rather than stored with a broken timing.
 */
const toSeconds = (stamp: string): number => {
  const parts = stamp.trim().split(':')

  if (parts.length < 2 || parts.length > 3) return NaN

  const seconds = parts.map(Number)

  if (seconds.some(Number.isNaN)) return NaN

  return seconds.reduce((total, part) => total * 60 + part, 0)
}

const parseBlock = (block: string): SubtitleCue | null => {
  const lines = block.split('\n')
  const timingIndex = lines.findIndex(line => line.includes(TIMESTAMP_SEPARATOR))

  // No timing means this is not a cue: the `WEBVTT` header, a metadata block, or junk such as
  // an error page served where a `.vtt` was expected.
  if (timingIndex === -1) return null

  const [rawStart, rawEnd] = lines[timingIndex].split(TIMESTAMP_SEPARATOR)

  const start = toSeconds(rawStart)
  // Cue settings (`line:90% align:center`) ride on the end timestamp and are positioning we
  // do not honour — the cue is rendered in our own panel, not by the user agent.
  const end = toSeconds(rawEnd?.trim().split(/\s+/)[0] ?? '')

  if (Number.isNaN(start) || Number.isNaN(end)) return null

  const text = lines
    .slice(timingIndex + 1)
    .join('\n')
    .replace(INLINE_TAG, '')
    .trim()

  // A timing with nothing to say would blank the panel mid-sentence.
  if (!text) return null

  return { start, end, text }
}

/**
 * The cues of a WebVTT file, in order.
 *
 * Hand-rolled rather than pulled from npm: the format's cue subset is sixty lines, and a new
 * dependency would have to clear `bunfig.toml`'s `minimumReleaseAge` gate for it.
 *
 * Never throws. A file that fails to parse yields no cues, which the caller already has to
 * handle — it is the same outcome as a language with no track.
 */
export const parseWebVTT = (source: string): SubtitleCue[] => {
  if (!source) return []

  return (
    source
      // Every `.vtt` measured on the Bunny CDN starts with a byte-order mark, which is what
      // makes a naive `startsWith('WEBVTT')` check fail.
      .replace(/^﻿/, '')
      .replace(/\r\n/g, '\n')
      .split(/\n{2,}/)
      .filter(block => block.trim() && !METADATA_BLOCK.test(block.trim()))
      .map(parseBlock)
      .filter((cue): cue is SubtitleCue => cue !== null)
  )
}

const covers = (cue: SubtitleCue | undefined, time: number): boolean => !!cue && time >= cue.start && time < cue.end

/**
 * The index of the cue covering `time`, or `-1` in a gap, before the first or after the last.
 *
 * `hint` is the previously active index. Playback moves forward one cue at a time, so checking
 * the hint and its successor first makes the common case two comparisons; a seek falls through
 * to a binary search. The caller polls this on every animation frame, so the fast path is the
 * reason the poll is affordable.
 *
 * `start` is inclusive and `end` exclusive, so two cues sharing a boundary cannot both match
 * and make the panel flicker.
 */
export const findCueAt = (cues: SubtitleCue[], time: number, hint?: number): number => {
  if (!cues.length) return -1

  if (typeof hint === 'number' && hint >= 0 && hint < cues.length) {
    if (covers(cues[hint], time)) return hint
    if (covers(cues[hint + 1], time)) return hint + 1
  }

  let low = 0
  let high = cues.length - 1

  while (low <= high) {
    const middle = (low + high) >> 1
    const cue = cues[middle]

    if (time < cue.start) high = middle - 1
    else if (time >= cue.end) low = middle + 1
    else return middle
  }

  return -1
}

/**
 * The one subtitle track to show a viewer, or `null` for no captions at all.
 *
 * Driven by the payload rather than by a list of supported languages in the client: the
 * backend already says which tracks exist, and a second list here would be another thing to
 * keep in step with `settings.LANGUAGES`.
 *
 * `null` — meaning the video plays with no panel — covers four cases deliberately treated
 * alike, because none of them is an error a viewer can act on: the video is still encoding,
 * the viewer's language has no track, there are no tracks at all, and the viewer is English.
 *
 * English is excluded because it is the source the other nine are translated from, not a track
 * anyone is meant to watch. `caption_payload()` already drops it server-side; the check here is
 * the second line of defence, and it also covers a hand-uploaded `en.vtt`.
 */
export const selectCaptionTrack = (
  video: Video | null | undefined,
  locale: string | null | undefined
): CaptionTrack | null => {
  if (!video?.url || !video?.ready) return null

  const wanted = baseLanguage(locale)

  if (!wanted || wanted === 'en') return null

  const candidates = (video.captions ?? []).filter(
    caption => caption?.srclang && caption?.url && baseLanguage(caption.srclang) === wanted
  )

  if (!candidates.length) return null

  const exact = candidates.find(caption => caption.srclang.toLowerCase() === locale.toLowerCase())

  return exact ?? candidates[0]
}

/** Whether a track's language is written right-to-left. */
export const isRtlCaption = (track: CaptionTrack | null | undefined): boolean =>
  RTL_LANGUAGES.includes(baseLanguage(track?.srclang))
